import { useCallback, useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

export interface FirebaseRestaurantItem {
	id: string;
	displayName: string;
	imageUrl: string;
}

interface UseRestaurantDetailsResult {
	restaurants: FirebaseRestaurantItem[];
	isLoading: boolean;
	error: Error | null;
	refetch: () => Promise<void>;
}

const normalizeRestaurant = (
	id: string,
	data: Record<string, any>
): FirebaseRestaurantItem => ({
	id: String(data?.id || id),
	displayName: String(data?.displayName || data?.name || ""),
	imageUrl: String(data?.imageUrl || data?.photoUrl || ""),
});

const fetchFromCollection = async (
	collectionName: string
): Promise<FirebaseRestaurantItem[]> => {
	const snapshot = await getDocs(collection(db, collectionName));

	return snapshot.docs
		.map((docItem) => {
			const data = docItem.data() as Record<string, any>;

			return {
				restaurant: normalizeRestaurant(docItem.id, data),
				status: String(data?.status || ""),
			};
		})
		.filter((entry) => entry.restaurant.displayName)
		.filter((entry) => !entry.status || entry.status.toLowerCase() === "approved")
		.map((entry) => entry.restaurant)
		.filter((item, index, allItems) => allItems.findIndex((x) => x.id === item.id) === index);
};

export const useRestaurantDetails = (): UseRestaurantDetailsResult => {
	const [restaurants, setRestaurants] = useState<FirebaseRestaurantItem[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);

	const loadRestaurants = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
				const upperCaseCollection = await fetchFromCollection("Restaurants");

				if (upperCaseCollection.length > 0) {
					setRestaurants(upperCaseCollection);
			} else {
					const lowerCaseCollection = await fetchFromCollection("restaurants");
					setRestaurants(lowerCaseCollection);
			}
		} catch (err) {
			setError(err as Error);
			setRestaurants([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadRestaurants();
	}, [loadRestaurants]);

	return {
		restaurants,
		isLoading,
		error,
		refetch: loadRestaurants,
	};
};

